import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart } from "./LineChart";
import { useState, useEffect, useMemo } from "react";
import { AlertCircle, Loader2, TrendingDown, TrendingUp, Minus, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * ConnectAnalytics - Citizen engagement analytics dashboard (wireframe/demo)
 *
 * Keep this version working for demos. The live version lives in
 * `ConnectAnalyticsLive` behind its own feature flag.
 *
 * Feature flag: :connect_analytics
 */
export function ConnectAnalytics() {
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [selectedWindow, setSelectedWindow] = useState<"7d" | "30d" | "quarter" | "ytd">("30d");

  // Simulate loading state for mockup
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const chartDataByWindow = {
    "7d": [
      { month: "Mon", questions: 58, timeSaved: 87 },
      { month: "Tue", questions: 64, timeSaved: 96 },
      { month: "Wed", questions: 71, timeSaved: 106 },
      { month: "Thu", questions: 67, timeSaved: 100 },
      { month: "Fri", questions: 79, timeSaved: 118 },
      { month: "Sat", questions: 45, timeSaved: 67 },
      { month: "Sun", questions: 41, timeSaved: 61 },
    ],
    "30d": [
      { month: "Week 1", questions: 318, timeSaved: 477 },
      { month: "Week 2", questions: 336, timeSaved: 504 },
      { month: "Week 3", questions: 352, timeSaved: 528 },
      { month: "Week 4", questions: 367, timeSaved: 550 },
    ],
    quarter: [
      { month: "Jan", questions: 912, timeSaved: 1368 },
      { month: "Feb", questions: 977, timeSaved: 1466 },
      { month: "Mar", questions: 1034, timeSaved: 1551 },
    ],
    ytd: [
      { month: "Jan", questions: 912, timeSaved: 1368 },
      { month: "Feb", questions: 977, timeSaved: 1466 },
      { month: "Mar", questions: 1034, timeSaved: 1551 },
      { month: "Apr", questions: 998, timeSaved: 1497 },
      { month: "May", questions: 1089, timeSaved: 1633 },
      { month: "Jun", questions: 1124, timeSaved: 1686 },
    ],
  } as const;

  const kpisByWindow = {
    "7d": [
      { label: "Resident interactions", value: "425", delta: "+8.2%", direction: "up", description: "Questions handled across all channels", tooltip: "Total resident questions received by Connect this period." },
      { label: "Self-service deflection", value: "71%", delta: "+2.3%", direction: "up", description: "Resolved without staff follow-up", tooltip: "Percent of inquiries answered without requiring a staff response." },
      { label: "Estimated staff hours saved", value: "119h", delta: "+9.1%", direction: "up", description: "Recovered frontline capacity", tooltip: "Estimated staff hours avoided using deflection assumptions for routine requests." },
      { label: "Median response speed", value: "2m 40s", delta: "-14s", direction: "up", description: "From resident ask to first answer", tooltip: "Middle value response time; lower is better." },
      { label: "Resident satisfaction trend", value: "4.4 / 5", delta: "+0.2", direction: "up", description: "Feedback quality signal", tooltip: "Weighted rating from thumbs-up signals and follow-up sentiment." },
    ],
    "30d": [
      { label: "Resident interactions", value: "1,373", delta: "+11.4%", direction: "up", description: "Questions handled across all channels", tooltip: "Total resident questions received by Connect this period." },
      { label: "Self-service deflection", value: "68%", delta: "+1.8%", direction: "up", description: "Resolved without staff follow-up", tooltip: "Percent of inquiries answered without requiring a staff response." },
      { label: "Estimated staff hours saved", value: "550h", delta: "+13.6%", direction: "up", description: "Recovered frontline capacity", tooltip: "Estimated staff hours avoided using deflection assumptions for routine requests." },
      { label: "Median response speed", value: "3m 05s", delta: "-21s", direction: "up", description: "From resident ask to first answer", tooltip: "Middle value response time; lower is better." },
      { label: "Resident satisfaction trend", value: "4.3 / 5", delta: "+0.1", direction: "up", description: "Feedback quality signal", tooltip: "Weighted rating from thumbs-up signals and follow-up sentiment." },
    ],
    quarter: [
      { label: "Resident interactions", value: "2,923", delta: "+15.0%", direction: "up", description: "Questions handled across all channels", tooltip: "Total resident questions received by Connect this period." },
      { label: "Self-service deflection", value: "66%", delta: "+2.7%", direction: "up", description: "Resolved without staff follow-up", tooltip: "Percent of inquiries answered without requiring a staff response." },
      { label: "Estimated staff hours saved", value: "1,551h", delta: "+18.2%", direction: "up", description: "Recovered frontline capacity", tooltip: "Estimated staff hours avoided using deflection assumptions for routine requests." },
      { label: "Median response speed", value: "3m 17s", delta: "+5s", direction: "down", description: "From resident ask to first answer", tooltip: "Middle value response time; lower is better." },
      { label: "Resident satisfaction trend", value: "4.2 / 5", delta: "0.0", direction: "flat", description: "Feedback quality signal", tooltip: "Weighted rating from thumbs-up signals and follow-up sentiment." },
    ],
    ytd: [
      { label: "Resident interactions", value: "6,134", delta: "+18.9%", direction: "up", description: "Questions handled across all channels", tooltip: "Total resident questions received by Connect this period." },
      { label: "Self-service deflection", value: "64%", delta: "+3.1%", direction: "up", description: "Resolved without staff follow-up", tooltip: "Percent of inquiries answered without requiring a staff response." },
      { label: "Estimated staff hours saved", value: "9,201h", delta: "+22.5%", direction: "up", description: "Recovered frontline capacity", tooltip: "Estimated staff hours avoided using deflection assumptions for routine requests." },
      { label: "Median response speed", value: "3m 29s", delta: "-7s", direction: "up", description: "From resident ask to first answer", tooltip: "Middle value response time; lower is better." },
      { label: "Resident satisfaction trend", value: "4.2 / 5", delta: "+0.1", direction: "up", description: "Feedback quality signal", tooltip: "Weighted rating from thumbs-up signals and follow-up sentiment." },
    ],
  } as const;

  const popularTopics = [
    { topic: "Building Permits", chats: 234, change: "+14%" },
    { topic: "Property Taxes", chats: 189, change: "+8%" },
    { topic: "Trash Collection", chats: 156, change: "+19%" },
    { topic: "Parking Regulations", chats: 134, change: "-3%" },
    { topic: "Business Licenses", chats: 112, change: "+5%" },
  ];

  const sentimentBreakdown = [
    { label: "Positive", value: 62, tone: "good" },
    { label: "Neutral", value: 26, tone: "neutral" },
    { label: "Negative", value: 12, tone: "bad" },
  ];

  const complaintAging = [
    { bucket: "0-24h", count: 41, status: "healthy" },
    { bucket: "1-3 days", count: 23, status: "watch" },
    { bucket: "4-7 days", count: 14, status: "watch" },
    { bucket: "8+ days", count: 7, status: "risk" },
  ];

  const districtPerformance = [
    { district: "North Ward", engagementRate: 79, responseSpeed: "2m 40s", status: "healthy" },
    { district: "West Ward", engagementRate: 71, responseSpeed: "3m 02s", status: "healthy" },
    { district: "Central Ward", engagementRate: 66, responseSpeed: "3m 22s", status: "watch" },
    { district: "East Ward", engagementRate: 52, responseSpeed: "4m 12s", status: "risk" },
  ];

  const briefingHighlights = [
    "Permit and sanitation requests now account for 52% of all resident questions, signaling high demand for clearer service content.",
    "Negative sentiment is concentrated around delayed road maintenance and missed pickup concerns in East Ward.",
    "FAQ adoption is reducing repetitive calls, with a 68% self-service resolution rate this month.",
  ];

  const recommendedActions = [
    { action: "Publish an East Ward service update post with roadwork timeline and complaint SLA", owner: "Comms", priority: "High", eta: "Today" },
    { action: "Pin permit checklist and zoning FAQ on website and social channels", owner: "Planning + Comms", priority: "Medium", eta: "This week" },
    { action: "Launch weekend sanitation reminder campaign before Thursday collection cycle", owner: "Public Works", priority: "High", eta: "48 hours" },
    { action: "Share monthly trust dashboard snapshot with council packet", owner: "City Manager", priority: "Low", eta: "Next council meeting" },
  ];

  const recentComplaints = [
    {
      complaint: "Potholes on Elm Street have not been repaired for weeks",
      timestamp: "10 minutes ago",
      category: "Road Maintenance",
    },
    {
      complaint: "Street lights are out near the community center.",
      timestamp: "45 minutes ago",
      category: "Public Safety",
    },
    {
      complaint: "Trash pickup missed our block again this week.",
      timestamp: "2 hours ago",
      category: "Sanitation",
    },
  ];

  const selectedChartData = useMemo(() => chartDataByWindow[selectedWindow], [selectedWindow]);
  const selectedKpis = useMemo(() => kpisByWindow[selectedWindow], [selectedWindow]);

  function getTrendIcon(direction: string) {
    if (direction === "up") return TrendingUp;
    if (direction === "down") return TrendingDown;
    return Minus;
  }

  function getPriorityClass(priority: string) {
    if (priority === "High") return "border-red-200 bg-red-50 text-red-700";
    if (priority === "Medium") return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  function getStatusBadge(status: string) {
    if (status === "healthy") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Healthy</Badge>;
    if (status === "watch") return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Watch</Badge>;
    return <Badge variant="destructive">Risk</Badge>;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-4 flex-shrink-0">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Connect Analytics</h1>
            <p className="text-muted-foreground">
              Executive communications snapshot (demo data)
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden p-4 pt-0">
          <div className="space-y-4" role="status" aria-label="Loading analytics">
            <div className="grid gap-3 grid-cols-5">
              {Array.from({ length: 5 }).map((_, index) => (
                <Card key={index}>
                  <CardHeader className="pb-3">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-8 w-16" />
                  </CardHeader>
                </Card>
              ))}
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading analytics...</span>
                </div>
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Error state (kept for parity, though this wireframe does not fetch)
  if (error) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-4 flex-shrink-0">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Connect Analytics</h1>
            <p className="text-muted-foreground">
              Executive communications snapshot (demo data)
            </p>
          </div>
        </div>
        <div className="flex-1 p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load analytics data. Please try again later.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-full w-full flex flex-col">
        <div className="p-4 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Connect Analytics</h1>
            <p className="text-muted-foreground">
              Executive communications snapshot (demo data)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Demo Data</Badge>
            <Badge variant="outline">Last updated: 10:42 AM</Badge>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-0">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">Executive Service Snapshot</CardTitle>
                    <CardDescription>
                      Key indicators for city communications performance
                    </CardDescription>
                  </div>
                  <Tabs value={selectedWindow} onValueChange={(value) => setSelectedWindow(value as "7d" | "30d" | "quarter" | "ytd")}>
                    <TabsList>
                      <TabsTrigger value="7d">7d</TabsTrigger>
                      <TabsTrigger value="30d">30d</TabsTrigger>
                      <TabsTrigger value="quarter">Quarter</TabsTrigger>
                      <TabsTrigger value="ytd">YTD</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-5">
                  {selectedKpis.map((kpi) => {
                    const TrendIcon = getTrendIcon(kpi.direction);
                    return (
                      <Card key={kpi.label} className="shadow-none border bg-muted/20">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span>{kpi.label}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" aria-label={`About ${kpi.label}`}>
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-60">
                                {kpi.tooltip}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <div className="mt-2 flex items-end justify-between gap-2">
                            <p className="text-2xl font-semibold">{kpi.value}</p>
                            <div
                              className={cn(
                                "inline-flex items-center gap-1 text-xs font-semibold",
                                kpi.direction === "up" && "text-emerald-700",
                                kpi.direction === "down" && "text-red-700",
                                kpi.direction === "flat" && "text-muted-foreground",
                              )}
                            >
                              <TrendIcon className="h-3.5 w-3.5" />
                              <span>{kpi.delta}</span>
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">{kpi.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4 grid-cols-1 xl:grid-cols-3">
              <Card className="xl:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Engagement Trend</CardTitle>
                  <CardDescription>Question volume and estimated staff capacity returned</CardDescription>
                </CardHeader>
                <CardContent className="h-72">
                  <LineChart
                    data={selectedChartData}
                    title="Questions Over Time"
                    description="Engagement trend by selected period"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Sentiment and Trust</CardTitle>
                  <CardDescription>How residents are feeling in recent interactions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sentimentBreakdown.map((item) => (
                    <div key={item.label} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span>{item.label}</span>
                        <span className="font-medium">{item.value}%</span>
                      </div>
                      <Progress
                        value={item.value}
                        className={cn(
                          item.tone === "good" && "[&>div]:bg-emerald-500",
                          item.tone === "neutral" && "[&>div]:bg-slate-500",
                          item.tone === "bad" && "[&>div]:bg-red-500",
                        )}
                      />
                    </div>
                  ))}
                  <Separator />
                  <Alert>
                    <AlertDescription>
                      Watch item: negative sentiment is trending around sanitation delays in East Ward.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 grid-cols-1 xl:grid-cols-3">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Top Service Demand</CardTitle>
                  <CardDescription>Most discussed resident topics this period</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Topic</TableHead>
                        <TableHead className="text-right">Volume</TableHead>
                        <TableHead className="text-right">WoW</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {popularTopics.map((item) => (
                        <TableRow key={item.topic}>
                          <TableCell className="font-medium">{item.topic}</TableCell>
                          <TableCell className="text-right">{item.chats}</TableCell>
                          <TableCell className="text-right">
                            <span className={cn("font-semibold", item.change.startsWith("-") ? "text-red-700" : "text-emerald-700")}>
                              {item.change}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Neighborhood Equity Lens</CardTitle>
                  <CardDescription>Engagement coverage and response parity by ward</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {districtPerformance.map((district) => (
                    <div key={district.district} className="space-y-1.5 rounded-lg border p-3">
                      <div className="flex items-center justify-between text-sm">
                        <p className="font-medium">{district.district}</p>
                        {getStatusBadge(district.status)}
                      </div>
                      <div className="text-xs text-muted-foreground">Engagement rate: {district.engagementRate}%</div>
                      <Progress value={district.engagementRate} className="h-1.5" />
                      <div className="text-xs text-muted-foreground">Median response: {district.responseSpeed}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Complaint Aging Risk</CardTitle>
                  <CardDescription>Open issue backlog by age bucket</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {complaintAging.map((item) => (
                    <div key={item.bucket} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span>{item.bucket}</span>
                        <span className="font-semibold">{item.count}</span>
                      </div>
                      <div className="mt-2">
                        <Progress value={(item.count / 41) * 100} className="h-1.5" />
                      </div>
                      <div className="mt-2">{getStatusBadge(item.status)}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 grid-cols-1 xl:grid-cols-3">
              <Card className="xl:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Briefing Highlights</CardTitle>
                  <CardDescription>Ready-to-use talking points for city communications officials</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {briefingHighlights.map((highlight, index) => (
                    <div key={index} className="rounded-lg border p-3">
                      <div className="flex items-start gap-2">
                        <Badge variant="secondary">Highlight {index + 1}</Badge>
                        <p className="text-sm leading-6">{highlight}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Recent Complaint Feed</CardTitle>
                  <CardDescription>Latest resident-submitted issues</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentComplaints.map((item, index) => (
                    <div key={index} className="rounded-lg border p-3">
                      <p className="text-sm font-medium leading-5">{item.complaint}</p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{item.timestamp}</span>
                        <span aria-hidden="true">•</span>
                        <span>{item.category}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Recommended Action Queue</CardTitle>
                <CardDescription>Operational actions to improve trust and reduce inbound volume</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>ETA</TableHead>
                      <TableHead className="text-right">Execute</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recommendedActions.map((item) => (
                      <TableRow key={item.action}>
                        <TableCell className="font-medium">{item.action}</TableCell>
                        <TableCell>{item.owner}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getPriorityClass(item.priority)}>
                            {item.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.eta}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">Draft update</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
